import {updateProductCart, triggerIgniteAction, AppContext} from '../state';
import { useNeuroMessages } from '../components';
import {useContext} from 'react';

/**
 * @typedef ProductsHookValues
 * @property {Object} products
 * @property {Set} productCart
 */
/**
 *
 * @return {ProductsHookValues}
 */
const useProducts = () => {
  const { products, productCart: stateProductCart } = useContext(AppContext);
  const productCart = Array.isArray(stateProductCart)
    ? new Set(stateProductCart)
    : stateProductCart;

  return {
    products,
    productCart
  };
};

/**
 * @typedef ProductHookValues
 * @property {React.Element[]} features
 * @property {boolean} isSelected
 * @property {string} name
 * @property {string} description
 * @property {string} descriptionAbbreviated
 * @property {React.Element[]} disclosures
 * @property {Function} onProductActionClick
 * @property {Function} onSelectClick
 * @property {string} url
 */
/**
 * Returns common attributes needed when rendering a product
 * @param {Object} product
 * @returns ProductHookValues
 */
function useProduct(product) {
  const productId = product?.id || product || 0;
  const messageContext = useNeuroMessages();
  const { productCart: appStateProductCart, dispatch } = useContext(AppContext);

  const productCart = Array.isArray(appStateProductCart)
    ? new Set(appStateProductCart)
    : appStateProductCart;
  const isSelected = (productCart || new Set([])).has(productId.toString());

  const onSelectClick = () => {
    dispatch(updateProductCart(productCart, productId, isSelected));
  };

  const onProductActionClick = ({
    actionName = 'productApplyClicked',
    completed = true,
    value,
    append = false
  }) => {
    const params = {
      actionName,
      productId,
      value: value ? value : completed ? 'completed' : 'started',
      append
    };
    dispatch( triggerIgniteAction(params) );
  };

  const getListOfProductDetails = (key) => {
    const result = [];
    let isSearching = true;
    let searchIndex = 1;
    while (isSearching) {
      const messageKey = `${productId}.${key}.${searchIndex}`;
      if (messageContext.hasMessage(messageKey)) {
        result.push(messageContext.formatIntlMessage({ id: messageKey }));
      } else {
        isSearching = false;
      }
      searchIndex++;
    }
    return result;
  };

  const name = messageContext.hasMessage(`${productId}.name`)
    ? messageContext.formatIntlMessage({ id: `${productId}.name` })
    : '';

  const description = messageContext.hasMessage(`${productId}.description`)
    ? messageContext.formatIntlMessage({ id: `${productId}.description` })
    : '';

  const descriptionAbbreviated = messageContext.hasMessage(`${productId}.description.abbreviated`)
    ? messageContext.formatIntlMessage({ id: `${productId}.description.abbreviated` })
    : '';

  const url = messageContext.getMessage(`${productId}.url`);

  return {
    description,
    descriptionAbbreviated,
    disclosures: getListOfProductDetails('disclosures'),
    features: getListOfProductDetails('features'),
    isSelected,
    name,
    onProductActionClick,
    onSelectClick,
    url,
  };
}

export { useProduct, useProducts };
